'use client';

import React, { useMemo } from 'react';
import type { MoodleCourse } from '@/types/moodle.types';
import CourseCard from './CourseCard';

interface CourseGridProps {
  courses: MoodleCourse[];
  activeCategory: string;
  activeSortBy: string;
  searchQuery: string;
}

function sortCourses(courses: MoodleCourse[], sortBy: string): MoodleCourse[] {
  const sorted = [...courses];
  switch (sortBy) {
    case 'newest':
      return sorted.filter((c) => c.isNew).concat(sorted.filter((c) => !c.isNew));
    case 'price-low':
      return sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
    case 'price-high':
      return sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
    case 'rating':
      return sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    default:
      return sorted.sort((a, b) => (b.enrolledCount || 0) - (a.enrolledCount || 0));
  }
}

export default function CourseGrid({
  courses,
  activeCategory,
  activeSortBy,
  searchQuery,
}: CourseGridProps) {
  const filtered = useMemo(() => {
    let result = [...courses];

    if (activeCategory !== 'all') {
      result = result.filter((c) => c.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          (c.creator && c.creator.toLowerCase().includes(q)) ||
          (c.tags && c.tags.some((t) => t.toLowerCase().includes(q)))
      );
    }

    return sortCourses(result, activeSortBy);
  }, [courses, activeCategory, activeSortBy, searchQuery]);

  if (filtered.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-14 h-14 rounded-2xl bg-brand-bg flex items-center justify-center mb-4">
          <svg
            className="w-6 h-6 text-brand-text-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
            />
          </svg>
        </div>
        <p className="text-brand-text font-semibold text-base">No courses found</p>
        <p className="text-brand-text-muted text-sm mt-1">Try a different search or category</p>
      </div>
    );
  }

  return (
    <div>
      <p className="text-xs text-brand-text-muted mb-4 font-medium">
        {filtered.length} course{filtered.length !== 1 ? 's' : ''} found
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((course, i) => (
          <CourseCard key={course.id} course={course} index={i} />
        ))}
      </div>
    </div>
  );
}
